import { en, type TranslationKey, type Translations } from './en';
import { hu } from './hu';

export type { TranslationKey, Translations };

export type LocaleCode = 'hu' | 'en';

export interface LocaleConfig {
  code: LocaleCode;
  /** Value for the `lang` attribute and `hreflang`. */
  htmlLang: string;
  /** Name of the language *in* that language, for the switcher. */
  label: string;
  /** BCP 47 tag for `Intl` — Hungarian writes 12,5 where English writes 12.5. */
  numberLocale: string;
  /** Open Graph wants an underscored tag with a region (`hu_HU`), unlike `hreflang`. */
  ogLocale: string;
  /**
   * Path prefix under the site root. Hungarian is the primary audience so it takes the
   * root and carries no prefix; English is nested.
   */
  prefix: string;
  /** Localised URL segments, so Hungarian readers get Hungarian URLs. */
  routes: { calculators: string; guides: string };
}

export const LOCALES: readonly LocaleConfig[] = [
  {
    code: 'hu',
    htmlLang: 'hu',
    label: 'Magyar',
    numberLocale: 'hu-HU',
    ogLocale: 'hu_HU',
    prefix: '',
    routes: { calculators: 'kalkulatorok', guides: 'utmutatok' },
  },
  {
    code: 'en',
    htmlLang: 'en',
    label: 'English',
    numberLocale: 'en-GB',
    ogLocale: 'en_GB',
    prefix: 'en/',
    routes: { calculators: 'calculators', guides: 'guides' },
  },
];

/** The locale served from `/`, and the one `x-default` points at. */
export const DEFAULT_LOCALE: LocaleCode = 'hu';

const TABLES: Record<LocaleCode, Translations> = { hu, en };

export function getLocale(code: LocaleCode): LocaleConfig {
  const found = LOCALES.find((l) => l.code === code);
  if (!found) throw new Error(`Unknown locale: ${code}`);
  return found;
}

/**
 * Look up a string and substitute any `{placeholders}`.
 *
 * Missing keys throw rather than falling back to English or to the key itself. A silent
 * fallback would let an untranslated string ship to production looking almost right; on a
 * site whose whole point is being readable in Hungarian, failing the build is better.
 */
export function t(
  locale: LocaleCode,
  key: TranslationKey,
  params?: Record<string, string | number>,
): string {
  const value = TABLES[locale][key];
  if (value === undefined) throw new Error(`Missing translation: ${locale}/${key}`);
  if (!params) return value;

  return value.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/**
 * `t` for keys assembled at runtime, e.g. `calc.${id}.field.${name}`.
 *
 * TypeScript cannot check those against `TranslationKey`, so this takes a plain string and
 * still throws when the key is absent. The completeness test in `i18n.test.ts` is what
 * turns that runtime throw into a build failure.
 */
export function tKey(
  locale: LocaleCode,
  key: string,
  params?: Record<string, string | number>,
): string {
  return t(locale, key as TranslationKey, params);
}

/** Curried `t` for code that works within one locale, which is most of it. */
export const translator =
  (locale: LocaleCode) =>
  (key: TranslationKey, params?: Record<string, string | number>): string =>
    t(locale, key, params);

/**
 * Site-root-relative path (no leading slash) for a page in a given locale.
 *
 * Returning paths *relative to the site root* rather than absolute URLs is what lets the
 * same build serve from `/` in production and `/palinka-kalkulator.github.io/` on staging.
 * Callers prepend either the generator's computed `../` depth or `import.meta.env.BASE_URL`.
 */
export function pagePath(
  locale: LocaleCode,
  page: 'home' | 'calculators' | 'guides',
  slug?: string,
): string {
  const { prefix, routes } = getLocale(locale);
  if (page === 'home') return prefix;
  const segment = page === 'calculators' ? routes.calculators : routes.guides;
  return slug ? `${prefix}${segment}/${slug}/` : `${prefix}${segment}/`;
}

/**
 * Path to one calculator's own page, e.g. `kalkulatorok/higitas/`.
 *
 * Each calculator gets a dedicated URL because people search for the specific job — "pálinka
 * hígítás kalkulátor", not "calculators". The slug comes from the translation table, so the
 * Hungarian and English addresses are both idiomatic in their own language.
 */
export function calculatorPath(locale: LocaleCode, calculatorId: string): string {
  return pagePath(locale, 'calculators', tKey(locale, `calc.${calculatorId}.slug`));
}
